<script>
import { cloneDeep } from 'lodash-es';
import { mapActions, mapState } from 'vuex';
import ItineraryPricesPriceIntervalSettings from './ItineraryPricesPriceIntervalSettings';
import ItineraryPricesTransportSearchBar from './ItineraryPricesTransportSearchBar';
import ItineraryPricesSelectedTransports from './ItineraryPricesSelectedTransports';
import ItineraryPricesCarRentalSearchBar from './ItineraryPricesCarRentalSearchBar';
import ItineraryPricesSelectedCarRentals from './ItineraryPricesSelectedCarRentals';
import ItineraryPricesPriceSettingsPax from './ItineraryPricesPriceSettingsPax';
import ItineraryPricesPriceSettingsTable from './ItineraryPricesPriceSettingsTable';
import ItineraryPricesTransportSearchFeed from './ItineraryPricesTransportSearchFeed';

export default {
  name: 'ItineraryPricesWrapper',
  components: {
    ItineraryPricesPriceIntervalSettings,
    ItineraryPricesTransportSearchBar,
    ItineraryPricesSelectedTransports,
    ItineraryPricesCarRentalSearchBar,
    ItineraryPricesSelectedCarRentals,
    ItineraryPricesPriceSettingsPax,
    ItineraryPricesPriceSettingsTable,
    ItineraryPricesTransportSearchFeed,
  },
  props: {
    itineraryPriceIntervals: {
      type: Array,
      default: () => [],
    },
    transits: {
      type: Array,
      required: true,
    },
    carriers: {
      type: Array,
      required: true,
    },
    dayProgram: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      localItineraryPriceIntervals: [],
      headers: null,
      ageDiscountPaxes: [],
      groupPaxes: [],
      transportStatus: {
        optional: 0,
        alternative: 1,
        primary: 2,
      },
      sync: false,
    };
  },
  created() {
    const itineraryId = +this.$route.params.id;
    this.getItineraryDomains(itineraryId).then(res => {
      this.getSuppliersWithCurrencyBySupplierType(res);
    });

    if (this.itineraryPriceIntervals.length) {
      this.localItineraryPriceIntervals = cloneDeep(this.itineraryPriceIntervals);
      this.getItineraryPriceSettings(this.localItineraryPriceIntervals[0].id);
      this.getCarRentalPriceIntervalsByIntervalId(this.localItineraryPriceIntervals[0].id);
      this.changePriceInterval(this.localItineraryPriceIntervals[0].id);
    }
  },
  computed: {
    ...mapState({
      priceSettings: state => state.itinerary.priceSettings.items,
      priceSettingsLoading: state => state.itinerary.priceSettings.loading,
      transports: state => state.nonDynamicTransport.searchTransports.items,
      selectedPriceIntervalIndex: state => state.itinerary.selectedPriceIntervalIndex,
    }),
    showPrices() {
      return !this.priceSettingsLoading
        && this.localItineraryPriceIntervals.length
        && this.localItineraryPriceIntervals.find(interval => interval.id === this.selectedPriceIntervalIndex).id;
    },
  },
  watch: {
    selectedPriceIntervalIndex: {
      deep: true,
      handler(value) {
        if (this.localItineraryPriceIntervals.length && this.localItineraryPriceIntervals.find(interval => interval.id === value).id) {
          this.ageDiscountPaxes = [];
          this.groupPaxes = [];
        }
      },
    },
    itineraryPriceIntervals() {
      this.localItineraryPriceIntervals = cloneDeep(this.itineraryPriceIntervals);
      this.selectedPriceIntervalIndex = this.localItineraryPriceIntervals[0].id;
      this.getItineraryPriceSettings(
        this.localItineraryPriceIntervals.find(interval => interval.id === this.selectedPriceIntervalIndex).id,
      );
    },
  },
  methods: {
    ...mapActions({
      getItineraryDomains: 'itinerary/getItineraryDomains',
      syncItineraryPriceIntervals: 'itinerary/syncItineraryPriceIntervals',
      getSuppliersWithCurrencyBySupplierType: 'nonDynamicTransport/getSuppliersWithCurrencyBySupplierType',
      getItineraryPriceSettings: 'itinerary/getItineraryPriceSettings',
      getItineraryMatrixPrices: 'itinerary/getItineraryMatrixPrices',
      searchTransportsAllotments: 'nonDynamicTransport/searchTransports',
      getCarRentalPriceIntervalsByIntervalId: 'itinerary/getCarRentalPriceIntervalsByIntervalId',
      changePriceInterval: 'itinerary/changePriceInterval',
    }),
    setPriceIntervals(value) {
      this.localItineraryPriceIntervals = value;
    },
    updateSelectedAllotmentPeriods(value) {
      this.localItineraryPriceIntervals.find(interval => interval.id === this.selectedPriceIntervalIndex)
        .transportAllotments = [ ...value ];
    },
    setMaxAllotment(value) {
      this.localItineraryPriceIntervals.find(interval => interval.id === this.selectedPriceIntervalIndex)
        .maxAllotments = +value;
    },
    syncPriceIntervals() {
      const domainId = +this.$route.params.domainId;
      const itineraryId = +this.$route.params.id;
      const priceIntervals = this.localItineraryPriceIntervals;
      const payload = { domainId, itineraryId, priceIntervals };
      this.syncItineraryPriceIntervals(payload).then(() => {
        this.getItineraryMatrixPrices({
          itineraryId,
          domainId,
        });
      });
    },
    setHeaders(value) {
      this.headers = value;
    },
    setAgeDiscountPaxes(value) {
      this.ageDiscountPaxes = value;
    },
    setGroupPaxes(value) {
      this.groupPaxes = value;
    },
    changeIntervalActiveStatus(priceIntervalIndex) {
      this.localItineraryPriceIntervals[priceIntervalIndex].active =
        !this.localItineraryPriceIntervals[priceIntervalIndex].active;
    },
    searchTransports(searchBody) {
      this.searchTransportsAllotments(searchBody);
    },
    addTransportAllotment(transportAllotment) {
      const { transportAllotments } = this.localItineraryPriceIntervals.find(interval => interval.id === this.selectedPriceIntervalIndex);
      let defaultIncluded = !transportAllotments.length
        ? this.transportStatus.primary
        : this.transportStatus.optional;

      if (transportAllotments.length) {
        const primaryTransport = transportAllotments.find(e => e.included === this.transportStatus.primary);
        const selectedTransportOutbound = transportAllotment.transport.outbound;
        const primaryTransportOutbound = primaryTransport.transportAllotment.transport.outbound;

        if (selectedTransportOutbound[selectedTransportOutbound.length - 1].arrivalTransitId ===
          primaryTransportOutbound[0].departureTransitId
        ) {
          defaultIncluded = this.transportStatus.optional;
        } else {
          defaultIncluded = this.transportStatus.alternative;
        }
      }

      transportAllotments.push({
        id: transportAllotment.id,
        included: defaultIncluded,
        transportAllotment,
      });
    },
    removeTransportAllotment(transportAllotment) {
      const { transportAllotments } = this.localItineraryPriceIntervals.find(interval => interval.id === this.selectedPriceIntervalIndex);
      const transportAllotmentIndex = transportAllotments.find(allotment =>
        allotment.transportAllotment.id === transportAllotment.id, );
      transportAllotments.splice(transportAllotmentIndex, 1);
    },
  },
};
</script>

<template>
  <div>
    <v-card class="pa-3">
      <itinerary-prices-price-interval-settings
        :selected-price-interval-index="selectedPriceIntervalIndex"
        :price-intervals="localItineraryPriceIntervals"
        :itinerary-length="dayProgram.length"
        @update="setPriceIntervals"
        @change-active-status="changeIntervalActiveStatus"
      />
      <v-divider class="mb-4" />

      <v-btn class="mt-3 ml-auto" color="success" @click="syncPriceIntervals">
        Save price ranges and transports
      </v-btn>
    </v-card>

    <v-card class="pa-3 mt-4">
      <itinerary-prices-transport-search-bar
        v-if="localItineraryPriceIntervals.length"
        :selected-price-interval="localItineraryPriceIntervals.find(interval => interval.id === selectedPriceIntervalIndex)"
        :departure-date="localItineraryPriceIntervals.find(interval => interval.id === selectedPriceIntervalIndex).arrivalFrom"
        :carriers="carriers"
        :transits="transits"
        :itinerary-length="dayProgram.length"
        @search="searchTransports"
      />
      <itinerary-prices-transport-search-feed
        v-if="localItineraryPriceIntervals.length"
        :transport-search-results="transports"
        :selected-allotments="localItineraryPriceIntervals.find(interval => interval.id === selectedPriceIntervalIndex)
        .transportAllotments"
        @add-transport="addTransportAllotment"
        @remove-transport="removeTransportAllotment"
      />
      <v-divider class="mb-2" />
      <itinerary-prices-selected-transports
        v-if="localItineraryPriceIntervals.find(interval => interval.id === selectedPriceIntervalIndex)"
        :allotments="localItineraryPriceIntervals.find(interval => interval.id === selectedPriceIntervalIndex).transportAllotments"
        :max-allotments="localItineraryPriceIntervals.find(interval => interval.id === selectedPriceIntervalIndex).maxAllotments"
        @update="updateSelectedAllotmentPeriods"
        @setMaxAllotment="setMaxAllotment"
      />
      <v-btn class="mt-3 ml-auto" color="success" @click="syncPriceIntervals">
        Save price ranges and transports
      </v-btn>
    </v-card>

    <v-card class="pa-3 mt-4">
      <itinerary-prices-car-rental-search-bar
        v-if="localItineraryPriceIntervals.length"
        :selected-price-interval="localItineraryPriceIntervals.find(interval => interval.id === selectedPriceIntervalIndex)"
        @save="getItineraryPriceSettings(localItineraryPriceIntervals.find(interval => interval.id === selectedPriceIntervalIndex).id)"
      />

      <itinerary-prices-selected-car-rentals
        v-if="localItineraryPriceIntervals.length"
        :selected-price-interval="localItineraryPriceIntervals.find(interval => interval.id === selectedPriceIntervalIndex)"
        @remove="getItineraryPriceSettings(localItineraryPriceIntervals.find(interval => interval.id === selectedPriceIntervalIndex).id)"
        @sync="sync = !sync"
      />
    </v-card>

    <v-card v-if="localItineraryPriceIntervals.length" class="pa-3 mt-4">
      <div class="text-xs-center">
        <v-progress-circular
          v-if="priceSettingsLoading"
          color="primary"
          indeterminate
        />
      </div>
      <template v-if="showPrices">
        <itinerary-prices-price-settings-pax
          v-if="localItineraryPriceIntervals.length"
          :age-discounts="ageDiscountPaxes"
          :group-paxes="groupPaxes"
          @update="setHeaders"
          @ageDiscount="setAgeDiscountPaxes"
          @set-discount-ages="setAgeDiscountPaxes"
        />
        <itinerary-prices-price-settings-table
          v-if="headers"
          :selected-price-interval="localItineraryPriceIntervals.find(interval => interval.id === selectedPriceIntervalIndex)"
          :start-date="localItineraryPriceIntervals.find(interval => interval.id === selectedPriceIntervalIndex).startDate"
          :price-interval-settings="priceSettings"
          :pax-headers="headers"
          :day-program="dayProgram"
          :carriers="carriers"
          :transits="transits"
          :age-discount-paxes="ageDiscountPaxes"
          :sync="sync"
          @ageDiscount="setAgeDiscountPaxes"
          @group-paxes="setGroupPaxes"
        />
      </template>
      <div v-if="!localItineraryPriceIntervals.find(interval => interval.id === selectedPriceIntervalIndex).id" class="text-xs-center">
        <h5>You have selected a price interval that hasn't been saved, please save to create its prices</h5>
      </div>
    </v-card>
  </div>
</template>



// WEBPACK FOOTER //
// src/components/itinerary/itineraryPrices/ItineraryPricesWrapper.vue